import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/hooks/useCart";
import { useAuth } from "@/hooks/useAuth";
import { useFavorites } from "@/hooks/useFavorites";
import { useRewards } from "@/hooks/useRewards";
import { useStoreSettings } from "@/hooks/useStoreSettings";
import { useThemeColor, hexAlpha } from "@/hooks/useThemeColor";
import { useDesignState } from "@/hooks/useDesignSystem";
import EbdaaLogo from "@/components/feature/EbdaaLogo";
import type { RewardsData } from "@/hooks/useRewards";

const TIER_STYLES: Record<RewardsData["tier"], { color: string; border: string; bg: string; icon: string; badgeBg: string; badgeText: string }> = {
  bronze:   { color: "text-amber-700",  border: "border-amber-700/60",  bg: "bg-amber-700/20",  icon: "ri-medal-line",       badgeBg: "bg-amber-700",  badgeText: "text-white" },
  silver:   { color: "text-gray-300",   border: "border-gray-300/60",   bg: "bg-gray-300/20",   icon: "ri-medal-2-line",     badgeBg: "bg-gray-400",   badgeText: "text-white" },
  gold:     { color: "text-amber-400",  border: "border-amber-400/60",  bg: "bg-amber-400/20",  icon: "ri-vip-crown-line",   badgeBg: "bg-amber-400",  badgeText: "text-black" },
  platinum: { color: "text-cyan-300",   border: "border-cyan-300/60",   bg: "bg-cyan-300/20",   icon: "ri-vip-diamond-line", badgeBg: "bg-cyan-400",   badgeText: "text-black" },
};

const navLinks = [
  { label: "منتجات", href: "#products" },
  { label: "الأجهزة", href: "#devices" },
  { label: "ايادي و إكسسوارات", href: "#accessories" },
  { label: "إتصال", href: "#contact" },
];

interface NavbarProps {
  barVisible?: boolean;
}

export default function Navbar({ barVisible = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { totalItems, openCart } = useCart();
  const { user, isLoggedIn } = useAuth();
  const { totalItems: favCount } = useFavorites();
  const { getRewards } = useRewards();
  const { settings } = useStoreSettings();
  const { brand } = useDesignState();
  const tc = useThemeColor();
  const navigate = useNavigate();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [userTier, setUserTier] = useState<RewardsData["tier"] | null>(null);

  // Navbar style from design settings
  const navbarStyle = brand.navbarStyle ?? 'dark';
  const navbarHeight = brand.navbarHeight === 'sm' ? 'h-14' : brand.navbarHeight === 'lg' ? 'h-24' : 'h-20';
  const showSearch = brand.navbarShowSearch !== false;
  const showWishlist = brand.navbarShowWishlist !== false;
  const showRewards = brand.navbarShowRewards !== false;
  const isSticky = brand.navbarSticky !== false;

  // Compute navbar background based on style
  const getNavbarBg = () => {
    if (!scrolled && navbarStyle === 'transparent') return 'bg-transparent';
    if (navbarStyle === 'light') return 'bg-white border-b border-gray-200';
    if (navbarStyle === 'colored') return '';
    if (navbarStyle === 'glass') return 'backdrop-blur-md border-b border-white/10';
    // dark (default)
    return scrolled ? 'bg-[#0a0a0a]/95 backdrop-blur-md border-b' : 'bg-transparent';
  };

  const getNavbarInlineStyle = (): React.CSSProperties => {
    if (navbarStyle === 'colored') {
      return { background: tc.primary, borderBottom: `1px solid ${hexAlpha(tc.primary, 0.3)}` };
    }
    if (navbarStyle === 'glass') {
      return { background: 'rgba(10,10,10,0.5)' };
    }
    if (scrolled && navbarStyle !== 'light') {
      return { borderBottomColor: hexAlpha(tc.primary, 0.1) };
    }
    return {};
  };

  const textColor = navbarStyle === 'light' ? 'text-gray-700' : 'text-gray-300';
  const logoTextColor = navbarStyle === 'light' ? '#1a1a1a' : '#ffffff';

  // Load user tier when logged in
  useEffect(() => {
    if (isLoggedIn && user?.id) {
      getRewards(user.id).then((r) => setUserTier(r?.tier ?? null));
    } else {
      setUserTier(null);
    }
  }, [isLoggedIn, user, getRewards]);

  useEffect(() => {
    const threshold = barVisible ? 100 : 60;
    const onScroll = () => setScrolled(window.scrollY > threshold);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [barVisible]);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const handleSearchOpen = () => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery("");
    }
  };

  const topOffset = barVisible ? "top-10" : "top-0";

  return (
    <header
      className={`${isSticky ? 'fixed' : 'absolute'} left-0 right-0 z-50 transition-all duration-300 ${topOffset} ${getNavbarBg()}`}
      style={getNavbarInlineStyle()}
    >
      <div className={`max-w-7xl mx-auto px-4 md:px-8 ${navbarHeight} flex items-center justify-between`}>
        {/* Mobile Menu Button */}
        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${textColor}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>

        {/* Nav Links Desktop */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNav(link.href)}
              className={`text-sm font-medium cursor-pointer whitespace-nowrap tracking-wide transition-colors duration-200 ${textColor}`}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = tc.primary}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = ''}
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={() => navigate("/deals")}
            className="text-red-400 hover:text-red-300 transition-colors duration-200 text-sm font-bold cursor-pointer whitespace-nowrap tracking-wide border border-red-400/30 hover:border-red-400/60 px-3 py-1 rounded-full flex items-center gap-1"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-flashlight-line text-xs"></i>
            العروض
          </button>
          <button
            onClick={() => navigate("/products")}
            className="transition-colors duration-200 text-sm font-bold cursor-pointer whitespace-nowrap tracking-wide px-3 py-1 rounded-full border"
            style={{
              color: tc.primary,
              borderColor: hexAlpha(tc.primary, 0.3),
              fontFamily: "'Cairo', sans-serif",
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.color = tc.primaryLight;
              (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(tc.primary, 0.6);
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.color = tc.primary;
              (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(tc.primary, 0.3);
            }}
          >
            تسوّق الكل
          </button>
        </nav>

        {/* Logo + Cart group */}
        <div className="flex items-center gap-4">
          {/* Search Button */}
          {showSearch && (
            <button
              onClick={handleSearchOpen}
              className={`w-10 h-10 flex items-center justify-center transition-colors cursor-pointer ${textColor}`}
              aria-label="بحث"
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = tc.primary}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = ''}
            >
              <i className="ri-search-line text-xl"></i>
            </button>
          )}

          {/* Favorites Button */}
          {showWishlist && (
            <button
              onClick={() => navigate("/favorites")}
              className={`relative w-10 h-10 flex items-center justify-center hover:text-red-400 transition-colors cursor-pointer ${textColor}`}
              aria-label="المفضلة"
            >
              <i className="ri-heart-line text-xl"></i>
              {favCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-red-500 text-white text-xs font-black rounded-full leading-none">
                  {favCount > 9 ? "9+" : favCount}
                </span>
              )}
            </button>
          )}

          {/* Track Order Button */}
          <button
            onClick={() => navigate("/track-order")}
            className={`hidden md:flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap`}
            style={{
              color: tc.primary,
              borderColor: hexAlpha(tc.primary, 0.3),
              background: hexAlpha(tc.primary, 0.06),
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.background = hexAlpha(tc.primary, 0.15);
              (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(tc.primary, 0.6);
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.background = hexAlpha(tc.primary, 0.06);
              (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(tc.primary, 0.3);
            }}
            aria-label="تتبع الطلب"
          >
            <i className="ri-map-pin-time-line text-sm"></i>
            تتبع طلبك
          </button>
          <button
            onClick={() => navigate("/track-order")}
            className={`md:hidden w-10 h-10 flex items-center justify-center transition-colors cursor-pointer ${textColor}`}
            aria-label="تتبع الطلب"
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = tc.primary}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = ''}
          >
            <i className="ri-map-pin-time-line text-xl"></i>
          </button>

          {/* Rewards Button */}
          {showRewards && (
            <button
              onClick={() => navigate("/rewards")}
              className={`w-10 h-10 flex items-center justify-center transition-colors cursor-pointer ${textColor}`}
              aria-label="المكافآت"
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = tc.primary}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = ''}
            >
              <i className="ri-vip-crown-line text-xl"></i>
            </button>
          )}

          {/* Account Button */}
          <button
            onClick={() => navigate(isLoggedIn ? "/account" : "/auth")}
            className={`relative flex items-center justify-center transition-colors cursor-pointer ${textColor}`}
            aria-label="الحساب"
            onMouseEnter={e => { if (!isLoggedIn) (e.currentTarget as HTMLElement).style.color = tc.primary; }}
            onMouseLeave={e => { if (!isLoggedIn) (e.currentTarget as HTMLElement).style.color = ''; }}
          >
            {isLoggedIn ? (
              <div className="relative">
                <div className={`w-8 h-8 flex items-center justify-center rounded-full border transition-all ${
                  userTier ? `${TIER_STYLES[userTier].bg} ${TIER_STYLES[userTier].border}` : ""
                }`}
                style={!userTier ? { background: hexAlpha(tc.primary, 0.2), borderColor: hexAlpha(tc.primary, 0.4) } : {}}
                >
                  <span className={`text-xs font-black ${userTier ? TIER_STYLES[userTier].color : ""}`}
                    style={!userTier ? { color: tc.primary, fontFamily: "'Cairo', sans-serif" } : { fontFamily: "'Cairo', sans-serif" }}>
                    {user?.name.charAt(0)}
                  </span>
                </div>
                {userTier && (
                  <div className={`absolute -bottom-1.5 -left-1.5 w-5 h-5 flex items-center justify-center rounded-full border-2 border-[#0a0a0a] ${TIER_STYLES[userTier].badgeBg} transition-all`}>
                    <i className={`${TIER_STYLES[userTier].icon} ${TIER_STYLES[userTier].badgeText} text-[9px]`}></i>
                  </div>
                )}
              </div>
            ) : (
              <div className="w-10 h-10 flex items-center justify-center">
                <i className="ri-user-line text-xl"></i>
              </div>
            )}
          </button>

          {/* Cart Button */}
          <button
            onClick={openCart}
            className={`relative w-10 h-10 flex items-center justify-center transition-colors cursor-pointer ${textColor}`}
            aria-label="السلة"
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = tc.primary}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = ''}
          >
            <i className="ri-shopping-cart-line text-xl"></i>
            {totalItems > 0 && (
              <span
                className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs font-black rounded-full leading-none"
                style={{ background: tc.primary, color: tc.textOnPrimary }}
              >
                {totalItems > 9 ? "9+" : totalItems}
              </span>
            )}
          </button>

          {/* Logo */}
          {settings.logoUrl ? (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="flex items-center cursor-pointer"
            >
              <img
                src={settings.logoUrl}
                alt={settings.logoText || 'Ebdaa'}
                className="h-10 max-w-[120px] object-contain"
                style={{ filter: `drop-shadow(0 0 4px ${hexAlpha(tc.primary, 0.3)})` }}
              />
            </a>
          ) : (
            <EbdaaLogo
              size="md"
              showText
              subText={settings.logoSubText || "GAME STORE"}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            />
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          className="md:hidden border-t px-4 py-4 flex flex-col gap-4"
          style={{
            background: navbarStyle === 'light' ? '#ffffff' : 'rgba(10,10,10,0.98)',
            borderTopColor: hexAlpha(tc.primary, 0.1),
          }}
        >
          {navLinks.map((link) => (
            <button key={link.href} onClick={() => handleNav(link.href)}
              className={`transition-colors text-right text-base py-2 border-b border-white/5 cursor-pointer whitespace-nowrap ${textColor}`}
              style={{ fontFamily: "'Cairo', sans-serif" }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = tc.primary}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = ''}>
              {link.label}
            </button>
          ))}
          <button onClick={() => { setMenuOpen(false); navigate("/products"); }}
            className="text-right text-base py-2 cursor-pointer whitespace-nowrap font-bold"
            style={{ color: tc.primary, fontFamily: "'Cairo', sans-serif" }}>
            تسوّق الكل
          </button>
          <button
            onClick={() => { setMenuOpen(false); navigate("/track-order"); }}
            className="flex items-center gap-3 w-full py-3 px-4 rounded-xl border transition-all cursor-pointer whitespace-nowrap"
            style={{
              color: tc.primary,
              borderColor: hexAlpha(tc.primary, 0.25),
              background: hexAlpha(tc.primary, 0.07),
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            <i className="ri-map-pin-time-line text-lg"></i>
            <span className="font-bold text-sm">تتبع طلبك</span>
            <i className="ri-arrow-left-line text-sm mr-auto opacity-50"></i>
          </button>
        </div>
      )}

      {/* Search Overlay */}
      {searchOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center pt-24 px-4"
          onClick={() => setSearchOpen(false)}>
          <div className="w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleSearchSubmit} className="relative">
              <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none">
                <i className="ri-search-line text-xl" style={{ color: tc.primary }}></i>
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="ابحث عن منتج..."
                className="w-full bg-[#141414] text-white placeholder-gray-600 rounded-2xl pr-12 pl-14 py-4 text-base focus:outline-none transition-colors border"
                style={{
                  borderColor: hexAlpha(tc.primary, 0.3),
                  fontFamily: "'Cairo', sans-serif",
                }}
                onFocus={e => (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(tc.primary, 0.6)}
                onBlur={e => (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(tc.primary, 0.3)}
                dir="rtl"
              />
              <button type="button" onClick={() => setSearchOpen(false)}
                className="absolute inset-y-0 left-4 flex items-center text-gray-500 hover:text-white transition-colors cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </form>
            <p className="text-gray-600 text-xs text-center mt-3" style={{ fontFamily: "'Cairo', sans-serif" }}>
              اضغط Enter للبحث • اضغط خارج النافذة للإغلاق
            </p>
          </div>
        </div>
      )}
    </header>
  );
}
